import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import * as XLSX from "xlsx";
import { filterRowsForCurrentScope, getCenterScopedUrl, shouldUseCenterScopedApi } from "../../utils/centerScope";
import { getAssignedCenterId } from "../../utils/userSession";
import useDataStore from "../../store/dataStore";

const BASE_URL = "https://mis.empowerplus.in";

export default function CandidatePage() {

    const token = localStorage.getItem("token");
    const assignedCenterId = getAssignedCenterId();
    const store = useDataStore();
    const CACHE_KEY = "candidates";

    /* ================= COMMON ================= */

    const [msg, setMsg] = useState({ type: "", text: "" });

    const flash = (type, text) => {
        setMsg({ type, text });
    };

    const authHeaders = useMemo(() => ({
        Authorization: `Bearer ${token}`
    }), [token]);

    const safeJson = async (res) => {
        const t = await res.text();
        try {
            return t ? JSON.parse(t) : [];
        } catch {
            return [];
        }
    };

    /* ================= EXCEL ================= */

    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(null);
    const [exporting, setExporting] = useState(false);

    const CHUNK_SIZE = 500;
    const PARALLEL = 2;

    const uploadExcel = async () => {
        if (!file) return flash("err", "Select Excel file");

        let dataRows;
        try {
            const buffer = await file.arrayBuffer();
            const wb = XLSX.read(buffer, { type: "array" });
            const ws = wb.Sheets[wb.SheetNames[0]];
            dataRows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
        } catch (err) {
            return flash("err", `Cannot read file: ${err.message}`);
        }

        if (dataRows.length < 2) return flash("err", "Excel file is empty or has no data rows");

        const headerRow = dataRows[0];
        const dataOnly = dataRows.slice(1);
        const totalRows = dataOnly.length;
        const totalChunks = Math.ceil(totalRows / CHUNK_SIZE);

        // ── DIAGNOSTIC LOGS — open browser Console (F12) to see these ──
        console.group("📋 [Candidate Upload] Excel Parsed");
        console.log("Total rows (including header):", dataRows.length);
        console.log("Data rows (excluding header):", dataOnly.length);
        console.log("Header row →", JSON.stringify(headerRow));
        console.log("Row 1 →", JSON.stringify(dataOnly[0]));
        console.log("Row 2 →", JSON.stringify(dataOnly[1]));

        const requiredHeaders = ["projectId", "centerId", "epBatchId", "beneficiaryId"];
        const missingHeaders = requiredHeaders.filter(h => !headerRow.includes(h));
        if (missingHeaders.length > 0) {
            console.error("❌ MISSING REQUIRED HEADERS:", missingHeaders);
            console.error("   Your Excel has these headers:", headerRow.filter(Boolean));
            console.error("   Backend needs EXACT camelCase: projectId, centerId, epBatchId, beneficiaryId, beneficiaryName ...");
        } else {
            console.log("✅ All required headers present");
        }

        // Check if data rows look blank
        const blankRows = dataOnly.filter(row => row.every(c => c === null || String(c).trim() === ""));
        console.log(`Blank rows detected: ${blankRows.length} / ${dataOnly.length}`);
        if (blankRows.length === dataOnly.length) {
            console.error("❌ ALL DATA ROWS ARE BLANK — Excel may have data in wrong sheet or rows are truly empty");
        }
        console.groupEnd();

        setUploading(true);
        setUploadProgress({ uploaded: 0, total: totalRows, chunk: 0, totalChunks });

        let totalInserted = 0, totalUpdated = 0, totalSkipped = 0, failed = false, firstError = "";

        const uploadOneChunk = async (i) => {
            const chunkRows = [headerRow, ...dataOnly.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE)];

            console.log(`🚀 [Chunk ${i + 1}/${totalChunks}] Sending ${chunkRows.length - 1} data rows...`);
            console.log("   First data row being sent →", JSON.stringify(chunkRows[1]));

            const res = await fetch(
                `${BASE_URL}/api/candidates/upload-json?actor=Rahul`,
                {
                    method: "POST",
                    headers: { ...authHeaders, "Content-Type": "application/json" },
                    body: JSON.stringify(chunkRows),
                }
            );
            if (res.status === 413) throw new Error(`Chunk ${i + 1}: Payload too large (413)`);

            const rawText = await res.text();
            console.log(`📥 [Chunk ${i + 1}] Raw server response:`, rawText);

            let data;
            try { data = JSON.parse(rawText); } catch { data = { raw: rawText }; }

            if (!res.ok) throw new Error(`Chunk ${i + 1}: ${data?.message || `HTTP ${res.status}`}`);

            const ins = data?.inserted ?? 0;
            const upd = data?.updated ?? 0;
            const skp = data?.skipped ?? 0;
            const skippedRows = data?.skippedRows ?? [];

            console.log(`✅ [Chunk ${i + 1}/${totalChunks}] Inserted: ${ins} | Updated: ${upd} | Skipped: ${skp}`);
            if (skippedRows.length > 0) {
                console.group(`⚠️ [Chunk ${i + 1}] Skipped rows (${skippedRows.length}):`);
                skippedRows.forEach(r => console.warn(r));
                console.groupEnd();
            }
            if (ins === 0 && skp === 0 && skippedRows.length === 0) {
                console.warn(`⚠️ [Chunk ${i + 1}] ALL zeros — likely all rows were blank or header names don't match backend. Check header row above.`);
            }
            return { inserted: ins, updated: upd, skipped: skp };
        };

        let uploadedSoFar = 0;
        for (let start = 0; start < totalChunks && !failed; start += PARALLEL) {
            const indices = Array.from({ length: Math.min(PARALLEL, totalChunks - start) }, (_, k) => start + k);
            setUploadProgress({ uploaded: uploadedSoFar, total: totalRows, chunk: start + indices.length, totalChunks });

            const results = await Promise.allSettled(indices.map(uploadOneChunk));
            for (const r of results) {
                if (r.status === "fulfilled") {
                    totalInserted += r.value.inserted;
                    totalUpdated  += r.value.updated ?? 0;
                    totalSkipped  += r.value.skipped;
                    uploadedSoFar += r.value.inserted + (r.value.updated ?? 0) + r.value.skipped;
                } else {
                    failed = true;
                    firstError = r.reason?.message || "Upload failed";
                }
            }
            setUploadProgress({ uploaded: uploadedSoFar, total: totalRows, chunk: start + indices.length, totalChunks, error: failed });
        }

        setUploading(false);
        if (!failed) {
            console.log(`[Upload Summary] Inserted: ${totalInserted} | Updated: ${totalUpdated} | Skipped: ${totalSkipped} | Total: ${totalRows}`);
            flash("ok", `Upload complete — Inserted: ${totalInserted.toLocaleString()}, Updated: ${totalUpdated.toLocaleString()}, Skipped: ${totalSkipped.toLocaleString()}`);
            setFile(null);
            setUploadProgress(null);
            store.invalidate(CACHE_KEY);
            fetchFirst100();
        } else {
            flash("err", firstError);
            setUploadProgress(prev => prev ? { ...prev, error: true } : null);
        }
    };

    /* ================= FETCH ================= */

    const [rows, setRows] = useState(() => store.getRows(CACHE_KEY));
    const [loading, setLoading] = useState(false);
    const [loadingAll, setLoadingAll] = useState(false);
    const [allLoaded, setAllLoaded] = useState(() => store.isAllLoaded(CACHE_KEY));
    const [search, setSearch] = useState("");
    const [visibleCount, setVisibleCount] = useState(100);
    const [totalCount, setTotalCount] = useState(null);

    const fetchFirst100 = async () => {
        if (store.isCached(CACHE_KEY)) {
            setRows(store.getRows(CACHE_KEY));
            setAllLoaded(store.isAllLoaded(CACHE_KEY));
            return;
        }
        setLoading(true);
        try {
            const res = await fetch(
                getCenterScopedUrl(BASE_URL, "/api/candidates?page=0&size=100"),
                { headers: authHeaders }
            );
            const data = await safeJson(res);
            const items = data?.content ?? (Array.isArray(data) ? data : []);
            setRows(items);
            setAllLoaded(false);
            store.setRows(CACHE_KEY, items, false);
        } catch {
            setRows([]);
        }
        setLoading(false);
    };

    const fetchAllCandidates = async () => {
        setLoadingAll(true);
        try {
            const res = await fetch(
                getCenterScopedUrl(BASE_URL, "/api/candidates/all"),
                { headers: authHeaders }
            );
            const data = await safeJson(res);
            const allRows = Array.isArray(data) ? data : [];
            setRows(allRows);
            setVisibleCount(allRows.length);
            setAllLoaded(true);
            store.setRows(CACHE_KEY, allRows, true);
        } catch {
            setRows([]);
        }
        setLoadingAll(false);
    };

    useEffect(() => {
        fetchFirst100();
    }, []);

    useEffect(() => {
        fetch(`${BASE_URL}/api/dashboard/counts`, { headers: authHeaders })
            .then(r => r.ok ? r.json() : null)
            .then(d => { if (d) setTotalCount(d.candidates ?? null); })
            .catch(() => {});
    }, []);

    /* ================= SEARCH ================= */

    useEffect(() => { setVisibleCount(100); }, [search]);

    const scopedRows = useMemo(() => filterRowsForCurrentScope(rows), [rows]);

    const filtered = useMemo(() => {

        const q = search.toLowerCase();

        return scopedRows.filter(r =>
            r.beneficiaryName?.toLowerCase().includes(q) ||
            r.beneficiaryMobileNumber?.toLowerCase().includes(q) ||
            r.projectName?.toLowerCase().includes(q) ||
            r.beneficiaryId?.toLowerCase().includes(q)
        );

    }, [scopedRows, search]);

    /* ================= DELETE ================= */

    const deleteCandidate = async (id) => {

        if (!window.confirm("Delete this candidate?")) return;

        await fetch(`${BASE_URL}/api/candidates/${id}?actor=Rahul`, {
            method: "DELETE",
            headers: authHeaders
        });

        flash("ok", "Candidate deleted");

        store.invalidate(CACHE_KEY);
        fetchAllCandidates();

    };

    /* ================= AUDIT ================= */

    const [auditOpen, setAuditOpen] = useState(false);
    const [auditRows, setAuditRows] = useState([]);

    const openAuditAll = async () => {

        setAuditOpen(true);

        const res = await fetch(`${BASE_URL}/api/candidates/audits`, {
            headers: authHeaders
        });

        setAuditRows(await safeJson(res));

    };

    /* ================= TEMPLATE ================= */

    const downloadTemplate = () => {
        const a = document.createElement("a");
        a.href = "/templates/candidate_upload_template.xlsx";
        a.download = "candidate_upload_template.xlsx";
        document.body.appendChild(a);
        a.click();
        a.remove();
    };

    const exportExcel = async () => {
        if (exporting) return;
        setExporting(true);
        try {
            const params = new URLSearchParams();
            if (shouldUseCenterScopedApi() && assignedCenterId) {
                params.set("centerId", assignedCenterId);
            }
            const url = `${BASE_URL}/api/candidates/export${params.toString() ? "?" + params.toString() : ""}`;
            const res = await fetch(url, { headers: authHeaders });
            if (!res.ok) throw new Error(`Export failed (${res.status})`);
            const blob = await res.blob();
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = "candidates.xlsx";
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(a.href);
        } catch (err) {
            flash("err", err.message || "Export failed");
        } finally {
            setExporting(false);
        }
    };

    /* ================= UI ================= */

    return (

        <div className="cmp-page">

            <div className="header">
                <h1>Candidate Management</h1>

                {/* <button className="btn" onClick={openAuditAll}>
                    View Audit Logs
                </button> */}

            </div>

            {msg.text && (
                <div className={`flash ${msg.type === "ok" ? "flash-ok" : "flash-err"}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span>{msg.text}</span>
                    <button onClick={() => setMsg({ type: "", text: "" })} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, fontWeight: "bold", lineHeight: 1, color: "inherit", opacity: 0.6, padding: "0 2px" }}>×</button>
                </div>
            )}

            {/* Upload */}

            <div className="card grid-4">

                <input
                    type="file"
                    onChange={e => setFile(e.target.files[0])}
                />

                <button className="btn btn-primary" onClick={uploadExcel} disabled={uploading}>
                    {uploading ? "Uploading..." : "Upload Excel"}
                </button>

                <button className="btn btn-outline" onClick={downloadTemplate}>
                    Download Excel Template
                </button>

                <button className="btn btn-outline" onClick={exportExcel} disabled={exporting}>
                    {exporting ? "Processing..." : "Export Excel"}
                </button>

            </div>

            {uploadProgress && (
                <div className="card" style={{ padding: "10px 16px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 6 }}>
                        <span>
                            {uploadProgress.error
                                ? "Upload failed"
                                : uploadProgress.chunk >= uploadProgress.totalChunks
                                    ? "Upload complete"
                                    : `Uploading chunk ${uploadProgress.chunk} / ${uploadProgress.totalChunks}…`}
                        </span>
                        <span>{uploadProgress.uploaded.toLocaleString()} / {uploadProgress.total.toLocaleString()} rows</span>
                    </div>
                    <div style={{ background: "#e5e7eb", borderRadius: 8, height: 8, overflow: "hidden" }}>
                        <div style={{
                            height: "100%",
                            borderRadius: 8,
                            background: uploadProgress.error ? "#ef4444" : "#2563eb",
                            width: `${uploadProgress.total ? Math.round((uploadProgress.uploaded / uploadProgress.total) * 100) : 0}%`,
                            transition: "width 0.3s"
                        }} />
                    </div>
                </div>
            )}

            {/* Table */}

            <div className="card">

                <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                    <input
                        className="search"
                        placeholder="Search beneficiary id / name / mobile / project..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        style={{ flex: "0 0 70%", marginBottom: 0 }}
                    />
                    {!allLoaded && (
                        <button
                            className="btn btn-outline"
                            onClick={fetchAllCandidates}
                            disabled={loadingAll}
                            style={{ flex: "0 0 calc(30% - 8px)", fontSize: "0.8rem", padding: "6px 10px" }}
                        >
                            {loadingAll ? "Loading…" : `Load All Data (${rows.length} loaded)`}
                        </button>
                    )}
                </div>
                {totalCount !== null && (
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", marginTop: 4, marginBottom: 8 }}>
                        Total: {totalCount.toLocaleString()}
                    </div>
                )}

                <div className="table-wrap">

                    <table className="ep-table">

                        <thead>

                            <tr>
                                <th>#</th>
                                {/* <th>ID</th> */}
                                <th>Beneficiary ID</th>
                                <th>Project</th>
                                <th>Center</th>
                                <th>Batch</th>
                                <th>Name</th>
                                <th>DOB</th>
                                <th>Gender</th>
                                <th>Mobile</th>
                                <th>Email</th>
                                <th>District</th>
                                <th>State</th>
                                <th>Jio-BP Contract?</th>
                                <th>Trainee Type</th>
                                <th>Fresher/Refresher</th>
                                <th>Phone Type</th>
                                <th>Vehicle No.</th>
                                <th>Mode of Training</th>
                                <th>Family Members Attending</th>
                                <th>Dependent Members</th>
                                <th className="actions">Actions</th>
                            </tr>

                        </thead>

                        <tbody>

                            {filtered.slice(0, visibleCount).map((r, i) => (

                                <tr key={r.id}>

                                    <td>{i + 1}</td>
                                    {/* <td>{r.id}</td> */}
                                    <td>{r.beneficiaryId}</td>
                                    <td>{r.projectName}</td>
                                    <td>{r.centerId}</td>
                                    <td>{r.epBatchId}</td>
                                    <td>{r.beneficiaryName}</td>
                                    <td>{r.dateOfBirth}</td>
                                    <td>{r.gender}</td>
                                    <td>{r.beneficiaryMobileNumber}</td>
                                    <td>{r.emailId}</td>
                                    <td>{r.district}</td>
                                    <td>{r.state}</td>
                                    <td>{r.jioBpContractEmployee}</td>
                                    <td>{r.traineeType}</td>
                                    <td>{r.fresherRefresher}</td>
                                    <td>{r.phoneType}</td>
                                    <td>{r.vehicleNumber}</td>
                                    <td>{r.modeOfAttendingTraining}</td>
                                    <td>{r.familyMembersAttendingTraining}</td>
                                    <td>{r.dependentFamilyMembers}</td>

                                    <td className="actions">

                                        <button
                                            className="btn btn-sm btn-danger"
                                            onClick={() => deleteCandidate(r.id)}
                                        >
                                            Delete
                                        </button>

                                    </td>

                                </tr>

                            ))}

                            {!filtered.length && (
                                <tr>
                                    <td colSpan="22" className="muted">
                                        {loading ? "Loading..." : "No records found"}
                                    </td>
                                </tr>
                            )}
                            {!allLoaded && filtered.length > 0 && (
                                <tr>
                                    <td colSpan="22" style={{ textAlign: "center", padding: "14px" }}>
                                        <button
                                            className="btn btn-outline"
                                            onClick={fetchAllCandidates}
                                            disabled={loadingAll}
                                        >
                                            {loadingAll ? "Loading all data…" : "Load All Data"}
                                        </button>
                                    </td>
                                </tr>
                            )}
                            {allLoaded && filtered.length > visibleCount && (
                                <tr>
                                    <td colSpan="22" style={{ textAlign: "center", padding: "14px" }}>
                                        <button
                                            className="btn btn-outline"
                                            onClick={() => setVisibleCount((c) => c + 500)}
                                        >
                                            Show More ({filtered.length - visibleCount} remaining)
                                        </button>
                                    </td>
                                </tr>
                            )}

                        </tbody>

                    </table>

                </div>

            </div>

            {/* AUDIT MODAL */}

            {auditOpen && createPortal(

                <div className="modal-overlay" onClick={() => setAuditOpen(false)}>

                    <div className="ep-modal wide" onClick={e => e.stopPropagation()}>

                        <h2>Candidate Audit Logs</h2>

                        <pre>{JSON.stringify(auditRows, null, 2)}</pre>

                    </div>

                </div>,

                document.body

            )}

            <style>{`

.cmp-page{
padding:22px;
background:#f7f7fb;
min-height:100vh;
font-family:Inter
}

.header{
display:flex;
justify-content:space-between;
align-items:center
}

h1{
font-size:26px;
font-weight:900
}

.flash{
padding:10px;
border-radius:10px;
margin:10px 0;
font-weight:700
}

.flash-ok{
background:#ecfdf5;
color:#065f46
}

.flash-err{
background:#fef2f2;
color:#991b1b
}

.card{
background:#fff;
border:1px solid #e5e7eb;
border-radius:14px;
padding:16px;
margin-bottom:14px
}

.grid-4{
display:grid;
grid-template-columns:1fr auto auto auto;
gap:10px
}

.btn{
border:1px solid #e5e7eb;
padding:10px 14px;
border-radius:12px;
font-weight:800;
cursor:pointer;
background:#fff
}

.btn-primary{
background:#2563eb;
color:#fff
}

.btn-sm{
padding:6px 10px;
font-size:12px
}

.btn-danger{
color:#991b1b;
border-color:#fca5a5
}

.search{
margin-bottom:10px;
padding:10px;
border-radius:12px;
border:1px solid #e5e7eb
}

.table-wrap{
overflow-x:auto
}

.ep-table{
width:100%;
min-width:1200px;
border-collapse:collapse
}

th,td{
padding:10px;
border-bottom:1px solid #e5e7eb;
font-size:13px;
white-space:nowrap
}

th{
background:#fcfcff;
text-align:left
}

td.actions{
position:sticky;
right:0;
background:#fff
}

.muted{
color:#6b7280;
font-weight:700
}

.modal-overlay{
position:fixed;
inset:0;
background:rgba(0,0,0,.45);
display:flex;
align-items:center;
justify-content:center;
z-index:99999
}

.ep-modal{
background:#fff;
padding:18px;
border-radius:16px;
width:900px;
max-height:90vh;
overflow:auto
}

`}</style>

        </div>

    );

}


// import React, { useEffect, useMemo, useState } from "react";
// import { createPortal } from "react-dom";
// import * as XLSX from "xlsx";
// import { filterRowsForCurrentScope, getCenterScopedUrl } from "../../utils/centerScope";

// const BASE_URL = "https://mis.empowerplus.in";

// export default function CandidatePage() {

//     const token = localStorage.getItem("token");

//     /* ================= COMMON ================= */

//     const [msg, setMsg] = useState({ type: "", text: "" });

//     const flash = (type, text) => {
//         setMsg({ type, text });
//     };

//     const authHeaders = useMemo(() => ({
//         Authorization: `Bearer ${token}`
//     }), [token]);

//     const safeJson = async (res) => {
//         const t = await res.text();
//         try {
//             return t ? JSON.parse(t) : [];
//         } catch {
//             return [];
//         }
//     };

//     /* ================= EXCEL ================= */

//     const [file, setFile] = useState(null);
//     const [uploading, setUploading] = useState(false);
//     const [uploadProgress, setUploadProgress] = useState(null);

//     const CHUNK_SIZE = 500;
//     const PARALLEL = 3;

//     const uploadExcel = async () => {
//         if (!file) return flash("err", "Select Excel file");

//         let dataRows;
//         try {
//             const buffer = await file.arrayBuffer();
//             const wb = XLSX.read(buffer, { type: "array" });
//             const ws = wb.Sheets[wb.SheetNames[0]];
//             dataRows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
//         } catch (err) {
//             return flash("err", `Cannot read file: ${err.message}`);
//         }

//         if (dataRows.length < 2) return flash("err", "Excel file is empty or has no data rows");

//         const headerRow = dataRows[0];
//         const dataOnly = dataRows.slice(1);
//         const totalRows = dataOnly.length;
//         const totalChunks = Math.ceil(totalRows / CHUNK_SIZE);

//         setUploading(true);
//         setUploadProgress({ uploaded: 0, total: totalRows, chunk: 0, totalChunks });

//         let totalInserted = 0, totalSkipped = 0, failed = false, firstError = "";

//         const uploadOneChunk = async (i) => {
//             const chunkRows = [headerRow, ...dataOnly.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE)];
//             const res = await fetch(
//                 `${BASE_URL}/api/candidates/upload-json?actor=Rahul`,
//                 {
//                     method: "POST",
//                     headers: { ...authHeaders, "Content-Type": "application/json" },
//                     body: JSON.stringify(chunkRows),
//                 }
//             );
//             if (res.status === 413) throw new Error(`Chunk ${i + 1}: Payload too large (413)`);
//             const data = await res.json();
//             if (!res.ok) throw new Error(`Chunk ${i + 1}: ${data?.message || `HTTP ${res.status}`}`);

//             const ins = data?.inserted ?? 0;
//             const skp = data?.skipped ?? 0;
//             const skippedRows = data?.skippedRows ?? [];

//             console.log(`[Chunk ${i + 1}/${totalChunks}] Inserted: ${ins} | Skipped: ${skp}`);
//             if (skippedRows.length > 0) {
//                 console.group(`[Chunk ${i + 1}] Skipped rows (${skippedRows.length}):`);
//                 skippedRows.forEach(r => console.log(r));
//                 console.groupEnd();
//             }
//             return { inserted: ins, skipped: skp };
//         };

//         let uploadedSoFar = 0;
//         for (let start = 0; start < totalChunks && !failed; start += PARALLEL) {
//             const indices = Array.from({ length: Math.min(PARALLEL, totalChunks - start) }, (_, k) => start + k);
//             setUploadProgress({ uploaded: uploadedSoFar, total: totalRows, chunk: start + indices.length, totalChunks });

//             const results = await Promise.allSettled(indices.map(uploadOneChunk));
//             for (const r of results) {
//                 if (r.status === "fulfilled") {
//                     totalInserted += r.value.inserted;
//                     totalSkipped += r.value.skipped;
//                     uploadedSoFar += r.value.inserted + r.value.skipped;
//                 } else {
//                     failed = true;
//                     firstError = r.reason?.message || "Upload failed";
//                 }
//             }
//             setUploadProgress({ uploaded: uploadedSoFar, total: totalRows, chunk: start + indices.length, totalChunks, error: failed });
//         }

//         setUploading(false);
//         if (!failed) {
//             console.log(`[Upload Summary] Inserted: ${totalInserted} | Skipped: ${totalSkipped} | Total: ${totalRows}`);
//             flash("ok", `Upload complete — Inserted: ${totalInserted.toLocaleString()}, Skipped: ${totalSkipped.toLocaleString()}`);
//             setFile(null);
//             setUploadProgress(null);
//             fetchAllCandidates();
//         } else {
//             flash("err", firstError);
//             setUploadProgress(prev => prev ? { ...prev, error: true } : null);
//         }
//     };

//     /* ================= FETCH ================= */

//     const [rows, setRows] = useState([]);
//     const [loading, setLoading] = useState(false);
//     const [loadingAll, setLoadingAll] = useState(false);
//     const [allLoaded, setAllLoaded] = useState(false);
//     const [search, setSearch] = useState("");
//     const [visibleCount, setVisibleCount] = useState(100);

//     const fetchFirst100 = async () => {
//         setLoading(true);
//         try {
//             const res = await fetch(
//                 getCenterScopedUrl(BASE_URL, "/api/candidates?page=0&size=100"),
//                 { headers: authHeaders }
//             );
//             const data = await safeJson(res);
//             setRows(data?.content ?? (Array.isArray(data) ? data : []));
//             setAllLoaded(false);
//         } catch {
//             setRows([]);
//         }
//         setLoading(false);
//     };

//     const fetchAllCandidates = async () => {
//         setLoadingAll(true);
//         try {
//             const res = await fetch(
//                 getCenterScopedUrl(BASE_URL, "/api/candidates/all"),
//                 { headers: authHeaders }
//             );
//             const data = await safeJson(res);
//             const allRows = Array.isArray(data) ? data : [];
//             setRows(allRows);
//             setVisibleCount(allRows.length);
//             setAllLoaded(true);
//         } catch {
//             setRows([]);
//         }
//         setLoadingAll(false);
//     };

//     useEffect(() => {
//         fetchFirst100();
//     }, []);

//     /* ================= SEARCH ================= */

//     useEffect(() => { setVisibleCount(100); }, [search]);

//     const scopedRows = useMemo(() => filterRowsForCurrentScope(rows), [rows]);

//     const filtered = useMemo(() => {

//         const q = search.toLowerCase();

//         return scopedRows.filter(r =>
//             r.beneficiaryName?.toLowerCase().includes(q) ||
//             r.beneficiaryMobileNumber?.toLowerCase().includes(q) ||
//             r.projectName?.toLowerCase().includes(q) ||
//             r.beneficiaryId?.toLowerCase().includes(q)
//         );

//     }, [scopedRows, search]);

//     /* ================= DELETE ================= */

//     const deleteCandidate = async (id) => {

//         if (!window.confirm("Delete this candidate?")) return;

//         await fetch(`${BASE_URL}/api/candidates/${id}?actor=Rahul`, {
//             method: "DELETE",
//             headers: authHeaders
//         });

//         flash("ok", "Candidate deleted");

//         fetchAllCandidates();

//     };

//     /* ================= AUDIT ================= */

//     const [auditOpen, setAuditOpen] = useState(false);
//     const [auditRows, setAuditRows] = useState([]);

//     const openAuditAll = async () => {

//         setAuditOpen(true);

//         const res = await fetch(`${BASE_URL}/api/candidates/audits`, {
//             headers: authHeaders
//         });

//         setAuditRows(await safeJson(res));

//     };

//     /* ================= TEMPLATE ================= */

//     const downloadTemplate = () => {
//         const a = document.createElement("a");
//         a.href = "/templates/candidate_upload_template.xlsx";
//         a.download = "candidate_upload_template.xlsx";
//         document.body.appendChild(a);
//         a.click();
//         a.remove();
//     };

//     const exportExcel = async () => {
//         let exportRows;
//         if (!allLoaded) {
//             setLoadingAll(true);
//             try {
//                 const res = await fetch(getCenterScopedUrl(BASE_URL, "/api/candidates/all"), { headers: authHeaders });
//                 const data = await safeJson(res);
//                 const allRows = Array.isArray(data) ? data : [];
//                 setRows(allRows);
//                 setVisibleCount(allRows.length);
//                 setAllLoaded(true);
//                 exportRows = filterRowsForCurrentScope(allRows);
//             } catch {
//                 exportRows = scopedRows;
//             }
//             setLoadingAll(false);
//         } else {
//             exportRows = scopedRows;
//         }
//         if (!exportRows.length) return flash("err", "No data to export");
//         const worksheet = XLSX.utils.json_to_sheet(exportRows);
//         const workbook = XLSX.utils.book_new();
//         XLSX.utils.book_append_sheet(workbook, worksheet, "Candidates");
//         XLSX.writeFile(workbook, "candidates.xlsx");
//     };

//     /* ================= UI ================= */

//     return (

//         <div className="cmp-page">

//             <div className="header">
//                 <h1>Candidate Management</h1>

//                 {/* <button className="btn" onClick={openAuditAll}>
//                     View Audit Logs
//                 </button> */}

//             </div>

//             {msg.text && (
//                 <div className={`flash ${msg.type === "ok" ? "flash-ok" : "flash-err"}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
//                     <span>{msg.text}</span>
//                     <button onClick={() => setMsg({ type: "", text: "" })} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, fontWeight: "bold", lineHeight: 1, color: "inherit", opacity: 0.6, padding: "0 2px" }}>×</button>
//                 </div>
//             )}

//             {/* Upload */}

//             <div className="card grid-4">

//                 <input
//                     type="file"
//                     onChange={e => setFile(e.target.files[0])}
//                 />

//                 <button className="btn btn-primary" onClick={uploadExcel} disabled={uploading}>
//                     {uploading ? "Uploading..." : "Upload Excel"}
//                 </button>

//                 <button className="btn btn-outline" onClick={downloadTemplate}>
//                     Download Excel Template
//                 </button>

//                 <button className="btn btn-outline" onClick={exportExcel} disabled={loadingAll}>
//                     {loadingAll ? "Loading..." : "Export Excel"}
//                 </button>

//             </div>

//             {uploadProgress && (
//                 <div className="card" style={{ padding: "10px 16px" }}>
//                     <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 6 }}>
//                         <span>
//                             {uploadProgress.error
//                                 ? "Upload failed"
//                                 : uploadProgress.chunk >= uploadProgress.totalChunks
//                                     ? "Upload complete"
//                                     : `Uploading chunk ${uploadProgress.chunk} / ${uploadProgress.totalChunks}…`}
//                         </span>
//                         <span>{uploadProgress.uploaded.toLocaleString()} / {uploadProgress.total.toLocaleString()} rows</span>
//                     </div>
//                     <div style={{ background: "#e5e7eb", borderRadius: 8, height: 8, overflow: "hidden" }}>
//                         <div style={{
//                             height: "100%",
//                             borderRadius: 8,
//                             background: uploadProgress.error ? "#ef4444" : "#2563eb",
//                             width: `${uploadProgress.total ? Math.round((uploadProgress.uploaded / uploadProgress.total) * 100) : 0}%`,
//                             transition: "width 0.3s"
//                         }} />
//                     </div>
//                 </div>
//             )}

//             {/* Table */}

//             <div className="card">

//                 <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
//                     <input
//                         className="search"
//                         placeholder="Search beneficiary id / name / mobile / project..."
//                         value={search}
//                         onChange={e => setSearch(e.target.value)}
//                         style={{ flex: "0 0 70%", marginBottom: 0 }}
//                     />
//                     {!allLoaded && (
//                         <button
//                             className="btn btn-outline"
//                             onClick={fetchAllCandidates}
//                             disabled={loadingAll}
//                             style={{ flex: "0 0 calc(30% - 8px)", fontSize: "0.8rem", padding: "6px 10px" }}
//                         >
//                             {loadingAll ? "Loading…" : `Load All Data (${rows.length} loaded)`}
//                         </button>
//                     )}
//                 </div>

//                 <div className="table-wrap">

//                     <table className="ep-table">

//                         <thead>

//                             <tr>
//                                 <th>#</th>
//                                 {/* <th>ID</th> */}
//                                 <th>Beneficiary ID</th>
//                                 <th>Project</th>
//                                 <th>Center</th>
//                                 <th>Batch</th>
//                                 <th>Name</th>
//                                 <th>DOB</th>
//                                 <th>Gender</th>
//                                 <th>Mobile</th>
//                                 <th>Email</th>
//                                 <th>District</th>
//                                 <th>State</th>
//                                 <th className="actions">Actions</th>
//                             </tr>

//                         </thead>

//                         <tbody>

//                             {filtered.slice(0, visibleCount).map((r, i) => (

//                                 <tr key={r.id}>

//                                     <td>{i + 1}</td>
//                                     {/* <td>{r.id}</td> */}
//                                     <td>{r.beneficiaryId}</td>
//                                     <td>{r.projectName}</td>
//                                     <td>{r.centerId}</td>
//                                     <td>{r.epBatchId}</td>
//                                     <td>{r.beneficiaryName}</td>
//                                     <td>{r.dateOfBirth}</td>
//                                     <td>{r.gender}</td>
//                                     <td>{r.beneficiaryMobileNumber}</td>
//                                     <td>{r.emailId}</td>
//                                     <td>{r.district}</td>
//                                     <td>{r.state}</td>

//                                     <td className="actions">

//                                         <button
//                                             className="btn btn-sm btn-danger"
//                                             onClick={() => deleteCandidate(r.id)}
//                                         >
//                                             Delete
//                                         </button>

//                                     </td>

//                                 </tr>

//                             ))}

//                             {!filtered.length && (
//                                 <tr>
//                                     <td colSpan="22" className="muted">
//                                         {loading ? "Loading..." : "No records found"}
//                                     </td>
//                                 </tr>
//                             )}
//                             {!allLoaded && filtered.length > 0 && (
//                                 <tr>
//                                     <td colSpan="22" style={{ textAlign: "center", padding: "14px" }}>
//                                         <button
//                                             className="btn btn-outline"
//                                             onClick={fetchAllCandidates}
//                                             disabled={loadingAll}
//                                         >
//                                             {loadingAll ? "Loading all data…" : "Load All Data"}
//                                         </button>
//                                     </td>
//                                 </tr>
//                             )}
//                             {allLoaded && filtered.length > visibleCount && (
//                                 <tr>
//                                     <td colSpan="22" style={{ textAlign: "center", padding: "14px" }}>
//                                         <button
//                                             className="btn btn-outline"
//                                             onClick={() => setVisibleCount((c) => c + 500)}
//                                         >
//                                             Show More ({filtered.length - visibleCount} remaining)
//                                         </button>
//                                     </td>
//                                 </tr>
//                             )}

//                         </tbody>

//                     </table>

//                 </div>

//             </div>

//             {/* AUDIT MODAL */}

//             {auditOpen && createPortal(

//                 <div className="modal-overlay" onClick={() => setAuditOpen(false)}>

//                     <div className="ep-modal wide" onClick={e => e.stopPropagation()}>

//                         <h2>Candidate Audit Logs</h2>

//                         <pre>{JSON.stringify(auditRows, null, 2)}</pre>

//                     </div>

//                 </div>,

//                 document.body

//             )}

//             <style>{`

// .cmp-page{
// padding:22px;
// background:#f7f7fb;
// min-height:100vh;
// font-family:Inter
// }

// .header{
// display:flex;
// justify-content:space-between;
// align-items:center
// }

// h1{
// font-size:26px;
// font-weight:900
// }

// .flash{
// padding:10px;
// border-radius:10px;
// margin:10px 0;
// font-weight:700
// }

// .flash-ok{
// background:#ecfdf5;
// color:#065f46
// }

// .flash-err{
// background:#fef2f2;
// color:#991b1b
// }

// .card{
// background:#fff;
// border:1px solid #e5e7eb;
// border-radius:14px;
// padding:16px;
// margin-bottom:14px
// }

// .grid-4{
// display:grid;
// grid-template-columns:1fr auto auto auto;
// gap:10px
// }

// .btn{
// border:1px solid #e5e7eb;
// padding:10px 14px;
// border-radius:12px;
// font-weight:800;
// cursor:pointer;
// background:#fff
// }

// .btn-primary{
// background:#2563eb;
// color:#fff
// }

// .btn-sm{
// padding:6px 10px;
// font-size:12px
// }

// .btn-danger{
// color:#991b1b;
// border-color:#fca5a5
// }

// .search{
// margin-bottom:10px;
// padding:10px;
// border-radius:12px;
// border:1px solid #e5e7eb
// }

// .table-wrap{
// overflow-x:auto
// }

// .ep-table{
// width:100%;
// min-width:1200px;
// border-collapse:collapse
// }

// th,td{
// padding:10px;
// border-bottom:1px solid #e5e7eb;
// font-size:13px;
// white-space:nowrap
// }

// th{
// background:#fcfcff;
// text-align:left
// }

// td.actions{
// position:sticky;
// right:0;
// background:#fff
// }

// .muted{
// color:#6b7280;
// font-weight:700
// }

// .modal-overlay{
// position:fixed;
// inset:0;
// background:rgba(0,0,0,.45);
// display:flex;
// align-items:center;
// justify-content:center;
// z-index:99999
// }

// .ep-modal{
// background:#fff;
// padding:18px;
// border-radius:16px;
// width:900px;
// max-height:90vh;
// overflow:auto
// }

// `}</style>

//         </div>

//     );

// }



// // import React, { useEffect, useMemo, useState } from "react";
// // import { createPortal } from "react-dom";
// // import * as XLSX from "xlsx";
// // import { filterRowsForCurrentScope, getCenterScopedUrl } from "../../utils/centerScope";

// // const BASE_URL = "https://mis.empowerplus.in";

// // export default function CandidatePage() {

// //     const token = localStorage.getItem("token");

// //     /* ================= COMMON ================= */

// //     const [msg, setMsg] = useState({ type: "", text: "" });

// //     const flash = (type, text) => {
// //         setMsg({ type, text });
// //     };

// //     const authHeaders = useMemo(() => ({
// //         Authorization: `Bearer ${token}`
// //     }), [token]);

// //     const safeJson = async (res) => {
// //         const t = await res.text();
// //         try {
// //             return t ? JSON.parse(t) : [];
// //         } catch {
// //             return [];
// //         }
// //     };

// //     /* ================= EXCEL ================= */

// //     const [file, setFile] = useState(null);
// //     const [uploading, setUploading] = useState(false);

// //     const uploadExcel = async () => {

// //         if (!file) return flash("err", "Select Excel file");

// //         const fd = new FormData();
// //         fd.append("file", file);

// //         setUploading(true);

// //         try {

// //             const res = await fetch(`${BASE_URL}/api/candidates/upload?actor=Rahul`, {
// //                 method: "POST",
// //                 headers: authHeaders,
// //                 body: fd
// //             });

// //             if (res.status === 413) {
// //                 flash("err", "File too large for server (413). Ask admin to increase nginx client_max_body_size.");
// //                 return;
// //             }
// //             if (!res.ok) {
// //                 const text = await res.text();
// //                 let errMsg = `Upload failed (${res.status})`;
// //                 try { const j = JSON.parse(text); errMsg = j.message || j.error || errMsg; } catch { }
// //                 flash("err", errMsg);
// //                 return;
// //             }

// //             const resText = await res.text();
// //             let resData; try { resData = resText ? JSON.parse(resText) : null; } catch { resData = null; }
// //             const cnt = resData?.successCount ?? resData?.inserted ?? resData?.totalRows ?? resData?.count ?? resData?.saved ?? resData?.rowsCreated ?? resData?.rowsInserted ?? null;
// //             flash("ok", `Excel uploaded${cnt != null ? ` — ${cnt} rows` : " successfully"}`);

// //             setFile(null);
// //             fetchCandidates();

// //         } catch (err) {

// //             flash("err", err.message || "Upload failed – check your connection");

// //         } finally {
// //             setUploading(false);
// //         }

// //     };

// //     /* ================= FETCH ================= */

// //     const [rows, setRows] = useState([]);
// //     const [loading, setLoading] = useState(false);
// //     const [search, setSearch] = useState("");
// //     const [visibleCount, setVisibleCount] = useState(100);

// //     const fetchCandidates = async () => {

// //         setLoading(true);

// //         try {

// //             const res = await fetch(getCenterScopedUrl(BASE_URL, "/api/candidates/all"), {
// //                 headers: authHeaders
// //             });

// //             // setRows(await safeJson(res));
// //             const data = await safeJson(res);
// //             setRows(data.content || data);
// //             console.log(data)

// //         } catch {
// //             setRows([]);
// //         }

// //         setLoading(false);

// //     };

// //     useEffect(() => {
// //         fetchCandidates();
// //     }, []);

// //     /* ================= SEARCH ================= */

// //     useEffect(() => { setVisibleCount(100); }, [search]);

// //     const scopedRows = useMemo(() => filterRowsForCurrentScope(rows), [rows]);

// //     const filtered = useMemo(() => {

// //         const q = search.toLowerCase();

// //         return scopedRows.filter(r =>
// //             r.beneficiaryName?.toLowerCase().includes(q) ||
// //             r.beneficiaryMobileNumber?.toLowerCase().includes(q) ||
// //             r.projectName?.toLowerCase().includes(q) ||
// //             r.beneficiaryId?.toLowerCase().includes(q)
// //         );

// //     }, [scopedRows, search]);

// //     /* ================= DELETE ================= */

// //     const deleteCandidate = async (id) => {

// //         if (!window.confirm("Delete this candidate?")) return;

// //         await fetch(`${BASE_URL}/api/candidates/${id}?actor=Rahul`, {
// //             method: "DELETE",
// //             headers: authHeaders
// //         });

// //         flash("ok", "Candidate deleted");

// //         fetchCandidates();

// //     };

// //     /* ================= AUDIT ================= */

// //     const [auditOpen, setAuditOpen] = useState(false);
// //     const [auditRows, setAuditRows] = useState([]);

// //     const openAuditAll = async () => {

// //         setAuditOpen(true);

// //         const res = await fetch(`${BASE_URL}/api/candidates/audits`, {
// //             headers: authHeaders
// //         });

// //         setAuditRows(await safeJson(res));

// //     };

// //     /* ================= TEMPLATE ================= */

// //     const downloadTemplate = () => {
// //         const a = document.createElement("a");
// //         a.href = "/templates/candidate_upload_template.xlsx";
// //         a.download = "candidate_upload_template.xlsx";
// //         document.body.appendChild(a);
// //         a.click();
// //         a.remove();
// //     };

// //     const [exporting, setExporting] = useState(false);

// //     const exportExcel = () => {
// //         if (!scopedRows.length) return flash("err", "No data to export");
// //         if (exporting) return;
// //         setExporting(true);
// //         setTimeout(() => {
// //             try {
// //                 const worksheet = XLSX.utils.json_to_sheet(scopedRows);
// //                 const workbook = XLSX.utils.book_new();
// //                 XLSX.utils.book_append_sheet(workbook, worksheet, "Candidates");
// //                 XLSX.writeFile(workbook, "candidates.xlsx");
// //             } finally {
// //                 setExporting(false);
// //             }
// //         }, 50);
// //     };

// //     /* ================= UI ================= */

// //     return (

// //         <div className="cmp-page">

// //             <div className="header">
// //                 <h1>Candidate Management</h1>

// //                 {/* <button className="btn" onClick={openAuditAll}>
// //                     View Audit Logs
// //                 </button> */}

// //             </div>

// //             {msg.text && (
// //                 <div className={`flash ${msg.type === "ok" ? "flash-ok" : "flash-err"}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
// //                     <span>{msg.text}</span>
// //                     <button onClick={() => setMsg({ type: "", text: "" })} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, fontWeight: "bold", lineHeight: 1, color: "inherit", opacity: 0.6, padding: "0 2px" }}>×</button>
// //                 </div>
// //             )}

// //             {/* Upload */}

// //             <div className="card grid-3">

// //                 <input
// //                     type="file"
// //                     onChange={e => setFile(e.target.files[0])}
// //                 />

// //                 <button className="btn btn-primary" onClick={uploadExcel}>
// //                     {uploading ? "Uploading..." : "Upload Excel"}
// //                 </button>

// //                 <button className="btn btn-outline" onClick={downloadTemplate}>
// //                     Download Excel Template
// //                 </button>

// //                 <button className="btn btn-outline" onClick={exportExcel} disabled={exporting}>
// //                     {exporting ? "Processing..." : "Export Excel"}
// //                 </button>

// //             </div>

// //             {/* Table */}

// //             <div className="card">

// //                 <input
// //                     className="search"
// //                     placeholder="Search beneficiary id / name / mobile / project..."
// //                     value={search}
// //                     onChange={e => setSearch(e.target.value)}
// //                 />

// //                 <div className="table-wrap">

// //                     <table className="ep-table">

// //                         <thead>

// //                             <tr>
// //                                 <th>#</th>
// //                                 {/* <th>ID</th> */}
// //                                 <th>Beneficiary ID</th>
// //                                 <th>Project</th>
// //                                 <th>Center</th>
// //                                 <th>Batch</th>
// //                                 <th>Name</th>
// //                                 <th>DOB</th>
// //                                 <th>Gender</th>
// //                                 <th>Mobile</th>
// //                                 <th>Email</th>
// //                                 <th>District</th>
// //                                 <th>State</th>
// //                                 <th className="actions">Actions</th>
// //                             </tr>

// //                         </thead>

// //                         <tbody>

// //                             {filtered.slice(0, visibleCount).map((r, i) => (

// //                                 <tr key={r.id}>

// //                                     <td>{i + 1}</td>
// //                                     {/* <td>{r.id}</td> */}
// //                                     <td>{r.beneficiaryId}</td>
// //                                     <td>{r.projectName}</td>
// //                                     <td>{r.centerId}</td>
// //                                     <td>{r.epBatchId}</td>
// //                                     <td>{r.beneficiaryName}</td>
// //                                     <td>{r.dateOfBirth}</td>
// //                                     <td>{r.gender}</td>
// //                                     <td>{r.beneficiaryMobileNumber}</td>
// //                                     <td>{r.emailId}</td>
// //                                     <td>{r.district}</td>
// //                                     <td>{r.state}</td>

// //                                     <td className="actions">

// //                                         <button
// //                                             className="btn btn-sm btn-danger"
// //                                             onClick={() => deleteCandidate(r.id)}
// //                                         >
// //                                             Delete
// //                                         </button>

// //                                     </td>

// //                                 </tr>

// //                             ))}

// //                             {!filtered.length && (
// //                                 <tr>
// //                                     <td colSpan="22" className="muted">
// //                                         {loading ? "Loading..." : "No records found"}
// //                                     </td>
// //                                 </tr>
// //                             )}
// //                             {filtered.length > visibleCount && (
// //                                 <tr>
// //                                     <td colSpan="22" style={{ textAlign: "center", padding: "14px" }}>
// //                                         <button
// //                                             className="btn btn-outline"
// //                                             onClick={() => setVisibleCount((c) => c + 100)}
// //                                         >
// //                                             Load More ({filtered.length - visibleCount} remaining)
// //                                         </button>
// //                                     </td>
// //                                 </tr>
// //                             )}

// //                         </tbody>

// //                     </table>

// //                 </div>

// //             </div>

// //             {/* AUDIT MODAL */}

// //             {auditOpen && createPortal(

// //                 <div className="modal-overlay" onClick={() => setAuditOpen(false)}>

// //                     <div className="ep-modal wide" onClick={e => e.stopPropagation()}>

// //                         <h2>Candidate Audit Logs</h2>

// //                         <pre>{JSON.stringify(auditRows, null, 2)}</pre>

// //                     </div>

// //                 </div>,

// //                 document.body

// //             )}

// //             <style>{`

// // .cmp-page{
// // padding:22px;
// // background:#f7f7fb;
// // min-height:100vh;
// // font-family:Inter
// // }

// // .header{
// // display:flex;
// // justify-content:space-between;
// // align-items:center
// // }

// // h1{
// // font-size:26px;
// // font-weight:900
// // }

// // .flash{
// // padding:10px;
// // border-radius:10px;
// // margin:10px 0;
// // font-weight:700
// // }

// // .flash-ok{
// // background:#ecfdf5;
// // color:#065f46
// // }

// // .flash-err{
// // background:#fef2f2;
// // color:#991b1b
// // }

// // .card{
// // background:#fff;
// // border:1px solid #e5e7eb;
// // border-radius:14px;
// // padding:16px;
// // margin-bottom:14px
// // }

// // .grid-3{
// // display:grid;
// // grid-template-columns:1fr auto auto auto;
// // gap:10px
// // }

// // .btn{
// // border:1px solid #e5e7eb;
// // padding:10px 14px;
// // border-radius:12px;
// // font-weight:800;
// // cursor:pointer;
// // background:#fff
// // }

// // .btn-primary{
// // background:#2563eb;
// // color:#fff
// // }

// // .btn-sm{
// // padding:6px 10px;
// // font-size:12px
// // }

// // .btn-danger{
// // color:#991b1b;
// // border-color:#fca5a5
// // }

// // .search{
// // margin-bottom:10px;
// // padding:10px;
// // border-radius:12px;
// // border:1px solid #e5e7eb
// // }

// // .table-wrap{
// // overflow-x:auto
// // }

// // .ep-table{
// // width:100%;
// // min-width:1200px;
// // border-collapse:collapse
// // }

// // th,td{
// // padding:10px;
// // border-bottom:1px solid #e5e7eb;
// // font-size:13px;
// // white-space:nowrap
// // }

// // th{
// // background:#fcfcff;
// // text-align:left
// // }

// // td.actions{
// // position:sticky;
// // right:0;
// // background:#fff
// // }

// // .muted{
// // color:#6b7280;
// // font-weight:700
// // }

// // .modal-overlay{
// // position:fixed;
// // inset:0;
// // background:rgba(0,0,0,.45);
// // display:flex;
// // align-items:center;
// // justify-content:center;
// // z-index:99999
// // }

// // .ep-modal{
// // background:#fff;
// // padding:18px;
// // border-radius:16px;
// // width:900px;
// // max-height:90vh;
// // overflow:auto
// // }

// // `}</style>

// //         </div>

// //     );

// // }
